import { Box, CheckboxOption, Input, Link, Select } from '@nerdwallet/currency';
import LinkIcon from '@nerdwallet/react-icon/material/Link';
import get from 'lodash/get';
import { usePathname, useSearchParams } from 'next/navigation';
import { Fragment, useEffect, useMemo } from 'react';
import SegmentImpression from '~/app/components/segment-impression';
import { useDynamicGrid } from '~/app/lib/redux/selectors/dynamic-grid-selector';
import { useIdentity } from '~/app/lib/redux/selectors/identity-selector';
import { isInBuilder } from '~/app/lib/helpers';
import { buttonUrlWithCustomQueryParams } from '../../dynamic-grid-helpers';
import { useModal } from '../../dynamic-grid-modal';
import {
  SegmentTrackingForm,
  SegmentTrackingProps,
} from '../../segment/segment-tracking-form';
import {
  addCtaApplyNowLinkTracking,
  useDynamicAnalytics,
} from '../../segment/use-dynamic-analytics';
import {
  DynamicTextComponent,
  DynamicTextComponentProps,
  DynamicTextForm,
  inputsSections,
} from '../dynamic-text/dynamic-text-component';
import { LINK_URL_SOURCE_TYPES, useDynamicLink } from './use-dynamic-link';

import styles from './dynamic-link.module.scss';

type DynamicLinkProps = {
  dynamicTextOptions?: DynamicTextComponentProps;
  url?: string;
  urlSource?: string;
  captureCustomQueryParams?: boolean;
  marketplaceOfferId?: string;
  authenticatedUrl?: string;
  linkOption?: string;
  segment?: SegmentTrackingProps;
  targetBlank?: boolean;
};

export const DynamicLink = ({
  dynamicTextOptions = { text: 'Link' },
  url = '',
  marketplaceOfferId,
  urlSource = LINK_URL_SOURCE_TYPES.CUSTOM_URL,
  linkOption = '',
  captureCustomQueryParams = false,
  authenticatedUrl = '',
  segment,
  targetBlank = false,
}: DynamicLinkProps) => {
  const searchParams = useSearchParams();
  const { isSignedIn } = useIdentity();
  const { marketplaceOffers } = useDynamicGrid();
  const { setUrl } = useDynamicLink();
  const isMarketplaceOffer =
    urlSource === LINK_URL_SOURCE_TYPES.MARKETPLACE_OFFER;
  const isModal = urlSource === LINK_URL_SOURCE_TYPES.MODAL;
  const { setHash } = useModal({ modalId: url });
  const pathname = usePathname();
  const isBuilderPage = isInBuilder(pathname);

  const colorValue =
    dynamicTextOptions.color === 'HEXCOLOR'
      ? dynamicTextOptions.hexColor
      : dynamicTextOptions.color;

  const selectedMarketplaceOffer = useMemo(() => {
    return marketplaceOffers?.find(
      (offer: any) => offer?.id === marketplaceOfferId
    );
  }, [marketplaceOffers, marketplaceOfferId]);

  let linkUrl = url;
  if (authenticatedUrl && isSignedIn) {
    linkUrl = authenticatedUrl;
  }

  const offerUrlLink = get(selectedMarketplaceOffer, linkOption, '');

  // Update the URL state when marketplace offer link changes
  useEffect(() => {
    if (isMarketplaceOffer && offerUrlLink) {
      setUrl(offerUrlLink);
    }
  }, [isMarketplaceOffer, offerUrlLink, setUrl]);

  if (isMarketplaceOffer && offerUrlLink) {
    linkUrl = offerUrlLink;
  }

  if (captureCustomQueryParams) {
    linkUrl = buttonUrlWithCustomQueryParams(linkUrl, searchParams).toString();
  }

  const {
    impressionViewProps,
    marketplaceButtonProps,
    handleTrackInteraction,
  } = useDynamicAnalytics({
    entityName: segment?.entityName || '',
    impressionSection: segment?.impressionSection || '',
    impressionPosition: segment?.impressionPosition || '',
    selectedMarketplaceOffer,
    modalId: isModal ? url : undefined,
    enableInteractionTracking: segment?.enableInteractionTracking,
  });

  const onClick = () => {
    handleTrackInteraction();
    if (isModal) setHash(url);
  };

  if (isBuilderPage && !linkUrl) {
    return <LinkIcon size={64} />;
  }

  if (!linkUrl) {
    return null;
  }

  if (segment?.enableInteractionTracking && selectedMarketplaceOffer?.id) {
    const updatedMarketPlaceEntity = addCtaApplyNowLinkTracking(
      { ...selectedMarketplaceOffer, cta: { link: linkUrl } },
      marketplaceButtonProps
    );
    linkUrl = updatedMarketPlaceEntity.cta.link;
  }

  const href = isModal ? undefined : linkUrl;

  return (
    <SegmentImpression eventName="Link Viewed" eventProps={impressionViewProps}>
      <div className={styles.link} style={{ color: colorValue }}>
        <Link
          href={href}
          target={targetBlank ? '_blank' : undefined}
          rel="noreferrer"
          onClick={onClick}
        >
          <DynamicTextComponent {...dynamicTextOptions} />
        </Link>
      </div>
    </SegmentImpression>
  );
};

export const DynamicLinkForm = () => {
  const {
    ctaLinkOptions,
    handleLinkChange,
    handleMarketplaceOfferIdChange,
    urlSourceOptions,
    url,
    urlSource,
    marketplaceOfferId,
    selectedLinkOption,
    handleUrlChange,
    handleUrlSourceChange,
    marketplaceOfferOptions,
    captureCustomQueryParams,
    handleCaptureCustomQueryParamsChange,
    handleAuthenticatedUrlChange,
    authenticatedUrl,
    targetBlank,
    handleTargetBlankChange,
  } = useDynamicLink();

  const disabledInputs = [] as inputsSections[];

  const isMarketplaceOfferLinkType =
    urlSource === LINK_URL_SOURCE_TYPES.MARKETPLACE_OFFER;
  const isCustomUrlLinkType = urlSource === LINK_URL_SOURCE_TYPES.CUSTOM_URL;
  const isModalLinkType = urlSource === LINK_URL_SOURCE_TYPES.MODAL;

  if (urlSource === LINK_URL_SOURCE_TYPES.MARKETPLACE_OFFER) {
    disabledInputs.push('marketplaceOffer');
  }

  return (
    <Box paddingY={2}>
      <Box paddingY={2}>
        <Select
          fullWidth
          value={urlSource}
          onChange={handleUrlSourceChange}
          label="Link Url Source"
          options={urlSourceOptions()}
        />
      </Box>
      {isModalLinkType && (
        <Box paddingY={2}>
          <Input
            label="Modal ID"
            fullWidth
            value={url}
            onChange={handleUrlChange}
          />
        </Box>
      )}
      {isCustomUrlLinkType && (
        <Fragment>
          <Box paddingY={2}>
            <Input
              label="Custom URL"
              fullWidth
              value={url}
              onChange={handleUrlChange}
            />
          </Box>
          <Box paddingY={2}>
            <Input
              label="Authenticated URL"
              fullWidth
              value={authenticatedUrl}
              onChange={handleAuthenticatedUrlChange}
            />
          </Box>
        </Fragment>
      )}
      {isMarketplaceOfferLinkType && (
        <Box paddingY={2}>
          <Select
            fullWidth
            value={marketplaceOfferId}
            onChange={handleMarketplaceOfferIdChange}
            label="Marketplace Offer"
            options={marketplaceOfferOptions()}
          />
        </Box>
      )}
      {isMarketplaceOfferLinkType && !!ctaLinkOptions?.length && (
        <Box paddingY={2}>
          <Select
            fullWidth
            value={selectedLinkOption}
            onChange={handleLinkChange}
            label="CTA Link"
            options={ctaLinkOptions}
          />
        </Box>
      )}
      <Box paddingY={2}>
        <CheckboxOption
          label="Capture Custom Query Params?"
          checked={captureCustomQueryParams}
          onChange={handleCaptureCustomQueryParamsChange}
        />
      </Box>
      <Box paddingY={2}>
        <CheckboxOption
          label="Open in New Tab?"
          checked={targetBlank}
          onChange={handleTargetBlankChange}
        />
      </Box>
      <SegmentTrackingForm />
      <Box paddingY={2}>
        <DynamicTextForm
          fieldPath="dynamicTextOptions"
          disabledInputs={disabledInputs}
          marketplaceOfferOverride={marketplaceOfferId}
        />
      </Box>
    </Box>
  );
};
